/**
 * Squarify treemap layout.
 * Input items: { id, value, ...extra }
 * Returns same items with x, y, width, height in the given rect.
 */
export function layoutTreemap(items, width, height) {
    const nodes = (items || [])
        .map((item) => ({
            ...item,
            value: Math.max(0, Number(item.value) || 0),
        }))
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value);

    if (!nodes.length || width <= 0 || height <= 0) {
        return [];
    }

    const total = nodes.reduce((sum, item) => sum + item.value, 0);
    const areas = nodes.map((item) => ({
        ...item,
        area: (item.value / total) * width * height,
    }));

    const result = [];

    function worst(row, side) {
        if (!row.length) {
            return Infinity;
        }
        const sum = row.reduce((s, item) => s + item.area, 0);
        let max = 0;
        let min = Infinity;
        row.forEach((item) => {
            max = Math.max(max, item.area);
            min = Math.min(min, item.area);
        });
        return Math.max(
            (side * side * max) / (sum * sum),
            (sum * sum) / (side * side * min),
        );
    }

    function layoutRow(row, x, y, w, h) {
        const sum = row.reduce((s, item) => s + item.area, 0);
        const horizontal = w >= h;
        if (horizontal) {
            const rowH = sum / w;
            let cursor = x;
            row.forEach((item) => {
                const itemW = item.area / rowH;
                result.push({
                    ...item,
                    x: cursor,
                    y,
                    width: itemW,
                    height: rowH,
                });
                cursor += itemW;
            });
            return { x, y: y + rowH, w, h: h - rowH };
        }
        const rowW = sum / h;
        let cursor = y;
        row.forEach((item) => {
            const itemH = item.area / rowW;
            result.push({
                ...item,
                x,
                y: cursor,
                width: rowW,
                height: itemH,
            });
            cursor += itemH;
        });
        return { x: x + rowW, y, w: w - rowW, h };
    }

    function squarify(children, x, y, w, h) {
        if (!children.length || w <= 0 || h <= 0) {
            return;
        }
        if (children.length === 1) {
            result.push({
                ...children[0],
                x,
                y,
                width: w,
                height: h,
            });
            return;
        }

        // Side along which the next row is laid (longer edge of remaining rect)
        const horizontal = w >= h;
        const side = horizontal ? w : h;
        const row = [];
        let remaining = children.slice();

        while (remaining.length) {
            const next = remaining[0];
            const withNext = row.concat(next);
            if (!row.length || worst(withNext, side) <= worst(row, side)) {
                row.push(next);
                remaining = remaining.slice(1);
            } else {
                break;
            }
        }

        const nextRect = layoutRow(row, x, y, w, h);
        squarify(remaining, nextRect.x, nextRect.y, nextRect.w, nextRect.h);
    }

    squarify(areas, 0, 0, width, height);
    return result;
}

export function heatColor(pl, maxAbs) {
    const value = Number(pl) || 0;
    const intensity = Math.min(1, Math.abs(value) / Math.max(maxAbs, 1));
    if (value >= 0) {
        const g = Math.round(120 + 90 * intensity);
        const r = Math.round(20 + 10 * (1 - intensity));
        return `rgb(${r}, ${g}, ${Math.round(70 + 40 * intensity)})`;
    }
    const r = Math.round(140 + 90 * intensity);
    const g = Math.round(40 + 20 * (1 - intensity));
    return `rgb(${r}, ${g}, ${Math.round(55 + 15 * (1 - intensity))})`;
}
